import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { sharedStyles, buttonStyles, layoutStyles, typographyStyles } from '../styles/shared.js';
import { storeApi, resolveProductImageUrl, type Product } from '@landi/store-core';
import './landi-product-card.js';

/**
 * Product showcase grid with category filter and pagination
 * Styled to match The Golden Peptides services section
 * 
 * @element landi-product-showcase
 * @fires product-click - Fired when a product is clicked
 * @fires add-to-cart - Fired when add to cart is clicked
 */
@customElement('landi-product-showcase')
export class LandiProductShowcase extends LitElement {
  static styles = [
    sharedStyles,
    buttonStyles,
    layoutStyles,
    typographyStyles,
    css`
      :host {
        display: block;
        padding: 4rem 0;
        background: var(--landi-surface);
      }
      
      .showcase-container {
        max-width: 64rem;
        margin: 0 auto;
        padding: 0 1.5rem;
      }
      
      @media (min-width: 640px) {
        .showcase-container {
          padding: 0 3rem;
        }
      }
      
      .showcase-header {
        text-align: center;
        margin-bottom: 3rem;
        max-width: 40rem;
        margin-left: auto;
        margin-right: auto;
      }
      
      .category-filters {
        display: flex;
        justify-content: center;
        flex-wrap: wrap;
        gap: 0.75rem;
        margin-bottom: 3rem;
      }
      
      .filter-btn {
        padding: 0.625rem 1.25rem;
        font-size: 0.75rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: var(--landi-text-muted);
        background: transparent;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        cursor: pointer;
        transition: all 200ms ease;
      }
      
      .filter-btn:hover {
        border-color: var(--landi-primary);
        color: var(--landi-primary);
      }
      
      .filter-btn.active {
        background: var(--landi-text);
        border-color: var(--landi-text);
        color: var(--landi-surface);
      }
      
      .products-grid {
        display: grid;
        grid-template-columns: repeat(1, 1fr);
        gap: 2rem;
        justify-content: center;
      }
      
      @media (min-width: 640px) {
        .products-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      
      @media (min-width: 1024px) {
        .products-grid {
          grid-template-columns: repeat(3, 1fr);
        }
      }
      
      .pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 0.5rem;
        margin-top: 3rem;
      }
      
      .page-btn {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--landi-text-muted);
        background: transparent;
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius);
        cursor: pointer;
        transition: all 200ms ease;
      }
      
      .page-btn:hover:not(:disabled) {
        border-color: var(--landi-primary);
        color: var(--landi-primary);
      }
      
      .page-btn.active {
        background: var(--landi-text);
        border-color: var(--landi-text);
        color: var(--landi-surface);
      }
      
      .page-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      
      .view-all {
        display: flex;
        justify-content: center;
        margin-top: 3rem;
      }
      
      .empty-state {
        text-align: center;
        padding: 4rem 2rem;
        color: var(--landi-text-muted);
      }
      
      .empty-state h3 {
        font-family: var(--landi-font-serif);
        font-size: 1.5rem;
        color: var(--landi-text);
        margin-bottom: 0.5rem;
      }
    `,
  ];

  @property({ type: String }) title = 'Our Products';
  @property({ type: String }) subtitle = 'Browse our collection of high-quality research compounds.';
  @property({ type: String }) category = '';
  @property({ type: Number, attribute: 'items-per-page' }) itemsPerPage = 6;
  @property({ type: Boolean, attribute: 'show-filters' }) showFilters = true;
  @property({ type: Boolean, attribute: 'show-pagination' }) showPagination = true;
  @property({ type: Boolean, attribute: 'featured-only' }) featuredOnly = false;
  @property({ type: String, attribute: 'view-all-href' }) viewAllHref = '/products';
  @property({ type: Boolean, attribute: 'show-view-all' }) showViewAll = false;
  @property({ type: String, attribute: 'product-href-base' }) productHrefBase = 'product.html?id=';

  @state() private currentPage = 1;
  @state() private selectedCategory = '';
  @state() private categories: string[] = [];
  @state() private products: Product[] = [];
  @state() private loading = true;
  @state() private loadError = '';

  connectedCallback() {
    super.connectedCallback();
    this.selectedCategory = this.category;
    void this.loadProducts();
  }

  private async loadProducts() {
    this.loading = true;
    this.loadError = '';
    try {
      if (this.featuredOnly) {
        this.products = await storeApi.getFeaturedProducts(this.itemsPerPage * 2);
      } else if (this.selectedCategory) {
        this.products = await storeApi.getProducts({ category: this.selectedCategory });
      } else {
        this.products = await storeApi.getProducts();
      }
      const cats = new Set<string>();
      this.products.forEach((p) => {
        if (p.category) cats.add(p.category);
      });
      this.categories = Array.from(cats);
    } catch (err) {
      this.loadError = err instanceof Error ? err.message : 'Failed to load products';
      this.products = [];
    } finally {
      this.loading = false;
    }
  }

  private get filteredProducts(): Product[] {
    return this.products;
  }

  private get paginatedProducts(): Product[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredProducts.slice(start, start + this.itemsPerPage);
  }

  private get totalPages(): number {
    return Math.ceil(this.filteredProducts.length / this.itemsPerPage);
  }

  private selectCategory(category: string) {
    this.selectedCategory = category;
    this.currentPage = 1;
    void this.loadProducts();
  }

  private goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  render() {
    const products = this.paginatedProducts;

    if (this.loading) {
      return html`<section class="showcase-container"><p class="section-subtitle">Loading products…</p></section>`;
    }
    if (this.loadError) {
      return html`<section class="showcase-container"><p class="section-subtitle">${this.loadError}</p></section>`;
    }

    return html`
      <section class="showcase-container">
        <header class="showcase-header">
          <span class="section-label">What We Offer</span>
          <h2 class="section-title">${this.title}</h2>
          <p class="section-subtitle">${this.subtitle}</p>
        </header>
        
        ${this.showFilters && this.categories.length > 1 ? html`
          <nav class="category-filters" aria-label="Product categories">
            <button 
              class="filter-btn ${!this.selectedCategory ? 'active' : ''}"
              @click=${() => this.selectCategory('')}
            >
              All
            </button>
            ${this.categories.map(cat => html`
              <button 
                class="filter-btn ${this.selectedCategory === cat ? 'active' : ''}"
                @click=${() => this.selectCategory(cat)}
              >
                ${cat}
              </button>
            `)}
          </nav>
        ` : ''}
        
        ${products.length > 0 ? html`
          <div class="products-grid">
            ${products.map(product => html`
              <landi-product-card
                product-id=${product.id}
                .product=${product}
                name=${product.name}
                price=${product.price}
                .comparePrice=${product.compareAtPrice}
                image=${resolveProductImageUrl(product.images[0]?.url) || ''}
                sku=${product.sku || ''}
                category=${product.category || ''}
                description=${product.shortDescription || ''}
                rating=${product.metadata?.rating || 0}
                reviews=${product.metadata?.reviews || 0}
                .inStock=${product.inventoryQuantity > 0}
                .featured=${product.featured}
                format=${product.metadata?.format || ''}
                purity=${product.metadata?.purity || ''}
                test-method=${product.metadata?.testMethod || ''}
                quantity=${product.variants?.[0]?.name || ''}
                href="${this.productHrefBase}${product.id}"
              ></landi-product-card>
            `)}
          </div>
          
          ${this.showPagination && this.totalPages > 1 ? html`
            <nav class="pagination" aria-label="Pagination">
              <button 
                class="page-btn"
                ?disabled=${this.currentPage === 1}
                @click=${() => this.goToPage(this.currentPage - 1)}
                aria-label="Previous page"
              >
                ←
              </button>
              
              ${Array.from({ length: this.totalPages }, (_, i) => i + 1).map(page => html`
                <button 
                  class="page-btn ${this.currentPage === page ? 'active' : ''}"
                  @click=${() => this.goToPage(page)}
                  aria-label="Page ${page}"
                  aria-current=${this.currentPage === page ? 'page' : 'false'}
                >
                  ${page}
                </button>
              `)}
              
              <button 
                class="page-btn"
                ?disabled=${this.currentPage === this.totalPages}
                @click=${() => this.goToPage(this.currentPage + 1)}
                aria-label="Next page"
              >
                →
              </button>
            </nav>
          ` : ''}
          
          ${this.showViewAll ? html`
            <div class="view-all">
              <a href=${this.viewAllHref} class="btn btn-secondary btn-lg">
                View Full Catalog
              </a>
            </div>
          ` : ''}
        ` : html`
          <div class="empty-state">
            <h3>No products found</h3>
            <p>Try adjusting your filters or check back later.</p>
          </div>
        `}
      </section>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landi-product-showcase': LandiProductShowcase;
  }
}
